/**
 * Every way out of this flow ends at the client's callback, and the redirect that gets there is
 * always one the server built. The authorize request is signed and verified against the client's
 * registered public key, so a `redirectUrl` in a response is a target the server only assembled
 * because the request proved who sent it — the browser navigates to it verbatim and holds no
 * validation rule of its own. The `redirectUri` sitting in this page's query is never a
 * destination: it is present on every request and says only what whoever wrote the link wanted.
 *
 * The exits that never reach the server have no such target and don't invent one. Declining, an
 * administrator stopped before consent, and a request that never arrived all end the same way: the
 * window closes. A client that opened this page through `launchWebAuthFlow` is watching for the
 * redirect, so a window that shuts without one fails the flow on its side — which is the refusal,
 * and leaves the user to start again deliberately rather than be handed a callback nothing signed.
 * Closing is refused for a page the client did not open, so those land on the dashboard instead.
 */
import {useEffect, useState} from 'react'
import {useParams, useNavigate, useSearchParams, useLocation} from 'react-router-dom'
import {jwtDecode} from 'jwt-decode'

import Paper from '@mui/material/Paper'

import Continue from './Continue'
import Loader from '../../shared/Loader'
import SingleSignOnError from './SingleSignOnError'
import SingleSignOnIntro from './SingleSignOnIntro'

import {asyncActions} from '../../../lib/asyncUtil'
import {connect} from '../../../state/hooks/tools'
import {CLIENTS, CONSENTS, CURRENT_USER, EMAILS, ROLES, SIGN_OUT} from '../../../state/actionTypes'
import {isEmpty, buildRoute} from '../../../lib/tools'
import {findRecordByAttribute} from '../../../lib/data'
import {clearSessionTokens, getToken} from '../../../lib/auth'
import {readEndpoint, createResource, deleteResource} from '../../../lib/asyncMethods'

import './singleSignOnController.scss'

const variantForStatus = ({statusCode, isAdmin}) => {
  if (statusCode === 400)
    return 'invalidRequest'

  if (statusCode === 401)
    return 'sessionExpired'

  if (statusCode === 403 && isAdmin)
    return 'adminAccount'

  return 'serverError'
}

const SingleSignOnController = props => {
  const navigate = useNavigate()
  const location = useLocation()
  const [query] = useSearchParams()
  const [currentUser, setCurrentUser] = useState(props.currentUser)
  const [authorizeResponse, setAuthorizeResponse] = useState(null)
  const [failure, setFailure] = useState({})
  const [mode, setMode] = useState('intro')
  const [client, setClient] = useState({})
  const clientId = query.get('clientId')
  const consent = findRecordByAttribute({records: props.consents, attribute: 'clientId', id: clientId})
  const email = findRecordByAttribute({records: props.emails, attribute: 'category', id: 'primary'})
  const role = props.roles[0] || {}
  const authToken = getToken('authToken')

  const authorizationAttributes = {
    responseType: 'code',
    codeChallenge: query.get('codeChallenge'),
    codeChallengeMethod: query.get('codeChallengeMethod'),
    clientId: query.get('clientId'),
    redirectUri: query.get('redirectUri'),
    scope: query.get('scope'),
    state: query.get('state')
  }

  useEffect(() => {
    if (isEmpty(currentUser)) {
      try {
        const {sub} = jwtDecode(authToken)

        props.fetchUsers(`users/${sub}`)
          .then(response => {
            if (response.success)
              setCurrentUser(response.data)

            if (!response.success)
              navigate(buildRoute({route: '/auth/sign-in', location}))
          })
          .catch(() => navigate(buildRoute({route: '/auth/sign-in', location})))
      } catch (error) {
        if (__DEV__)
          console.error(error)

        navigate(buildRoute({route: '/auth/sign-in', location}))
      }
    }

    if (isEmpty(consent))
      props.fetchConsents('consents')
  }, [])

  useEffect(() => {
    if (!isEmpty(currentUser)) {
      props.readEndpoint(`clients/${clientId}`).then(response => {
        if (response.success)
          setClient(response.data)

        if (!response.success)
          navigate('/not-found')
      })
      props.fetchEmails('emails')

      if (isEmpty(props.roles))
        props.fetchRoles('roles')
    }
  }, [currentUser])

  const redirectTo = redirectUrl => window.location.href = decodeURIComponent(redirectUrl)

  const fail = ({variant, statusCode, message}) => {
    setFailure({variant, statusCode, message})
    setMode('error')
  }

  const accept = () => {
    props.createResource({endpoint: 'sso/authorize', attributes: authorizationAttributes})
      .then(response => {
        if (response.success) {
          props.fetchConsents('consents')
          setMode('consent')
        } else {
          fail({
            variant: variantForStatus({statusCode: response.statusCode, isAdmin: role.name === 'admin'}),
            statusCode: response.statusCode,
            message: response.message
          })
        }

        setAuthorizeResponse(response.data)
      })
      .catch(() => fail({variant: 'networkError'}))
  }

  const terminate = () => {
    window.close()
    navigate('/dashboard')
  }

  const followRedirect = () => redirectTo(authorizeResponse.redirectUrl)

  const signOut = () => {
    props.signOut('auth/sign-out').catch(() => {}).then(() => {
      clearSessionTokens()
      Object.values(props.storeDispatchs).forEach(dispatch => dispatch(asyncActions(SIGN_OUT).success({data: {}})))
      navigate(buildRoute({route: '/auth/sign-in', location}))
    })
  }

  const signIn = () => navigate(buildRoute({route: '/auth/sign-in', location}))

  const renderError = variant => (
    <SingleSignOnError
      variant={variant}
      statusCode={failure.statusCode}
      message={failure.message}
      client={client}
      canReturnToClient={Boolean(authorizeResponse?.redirectUrl)}
      onReturnToClient={followRedirect}
      onSignOut={signOut}
      onSignIn={signIn}
      onRetry={accept}
      onCancel={terminate}
    />
  )

  const renderStep = () => {
    if (role.name === 'admin')
      return renderError('adminAccount')

    if (mode === 'error')
      return renderError(failure.variant)

    if (mode === 'intro')
      return <SingleSignOnIntro client={client} accept={accept} decline={terminate} />

    return <Continue currentUser={currentUser} email={email} client={client} onContinue={followRedirect} />
  }

  return (
    <Loader loading={isEmpty(client) || props.isLoading}>
      <Paper className='single-sign-on-controller' elevation={0} sx={{borderRadius: '40px'}}>
        {renderStep()}
      </Paper>
    </Loader>
  )
}

const mapStateToProps = ({clients, users, auth, consents, emails, roles}) => ({
  ...clients,
  ...consents,
  ...emails,
  ...users,
  roles: [],
  ...roles,
  currentUser: auth.user,
  isLoading: clients.loading || consents.loading || roles.loading
})

const actionCreators = ({clients, consents, users, emails, roles, auth, ...dispatchs}) => ({
  readEndpoint: readEndpoint(clients, CLIENTS),
  fetchConsents: readEndpoint(consents, CONSENTS),
  fetchUsers: readEndpoint(users, CURRENT_USER),
  fetchEmails: readEndpoint(emails, EMAILS),
  fetchRoles: readEndpoint(roles, ROLES),
  createResource: createResource(consents, 'AUTHORIZE'),
  signOut: deleteResource(auth, SIGN_OUT),
  storeDispatchs: {clients, consents, users, emails, roles, auth, ...dispatchs}
})

export default connect(mapStateToProps, actionCreators)(SingleSignOnController)
